<template>
  <div class="map-time-overlay">
    <div class="map-time-overlay-fields">
      <div class="map-time-overlay-field">
        <label for="map-time-start" class="map-time-overlay-label">{{ $t('timeControls.start') }}</label>
        <input
          id="map-time-start"
          type="datetime-local"
          class="map-time-overlay-input"
          :value="startDateStr"
          :min="metaStartStr"
          :max="endDateStr"
          @input="onStartChange"
        />
      </div>

      <div class="map-time-overlay-field">
        <span class="map-time-overlay-label map-time-overlay-label--with-info">
          <label for="map-time-end">{{ $t('timeControls.end') }}</label>
          <Button
            v-if="gridCoverageHint"
            v-tooltip.top="gridCoverageHint"
            icon="fas fa-circle-info"
            severity="secondary"
            text
            rounded
            size="small"
            class="map-time-overlay-info"
            :aria-label="gridCoverageHint"
            :title="gridCoverageHint"
            type="button"
          />
        </span>
        <input
          id="map-time-end"
          type="datetime-local"
          class="map-time-overlay-input"
          :value="endDateStr"
          :min="startDateStr"
          :max="metaEndStr"
          @input="onEndChange"
        />
      </div>
    </div>

    <div class="map-time-overlay-presets">
      <button
        v-for="h in presetHours"
        :key="h"
        class="map-time-overlay-preset-btn"
        @click="applyPreset(h)"
      >
        {{ h }}h
      </button>
    </div>

    <div class="map-time-overlay-actions">
      <Button
        v-tooltip.left="$t('timeControls.setStartToNow')"
        icon="fas fa-clock"
        severity="contrast"
        text
        size="small"
        @click="setStartToNow"
        :label="$t('timeControls.now')"
        class="map-time-overlay-now"
      />

      <Button
        v-tooltip.left="$t('timeControls.resetTimeRange')"
        icon="fas fa-arrow-rotate-left"
        severity="contrast"
        text
        size="small"
        @click="resetTimeRange"
      />

      <Button
        icon="fas fa-chart-area"
        :label="panelVisible ? $t('timeControls.hidePanel') : $t('timeControls.showPanel')"
        :severity="panelVisible ? 'primary' : 'secondary'"
        size="small"
        class="map-time-overlay-graph-btn"
        @click="$emit('toggle-panel')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import { useFilterStore } from '@/composables/useFilterStore';
import { useDateTimeSettings } from '@/composables/useDateTimeSettings';

const props = defineProps<{
  panelVisible: boolean;
}>();

defineEmits<{
  (e: 'toggle-panel'): void;
}>();

const { timeRange, metaTimeRange, standardForecastEndMs, mapViewMode, gridCoverage } = useFilterStore();
const { toDatetimeLocal, fromDatetimeLocal, formatDateTime } = useDateTimeSettings();
const { t } = useI18n();

/**
 * Shown only in grid view, because it is the only view the range can outrun.
 *
 * Swaths and AOI coverage are live queries over the predictor's served window, and
 * the pickers are already bounded by it — every satellite is available across the
 * whole selectable range there. The previous hint ("only OroraTech orbital paths
 * extend beyond 48 hours") was shown in every mode, which made it wrong in two of
 * the three, and it also understated the grid's own limits: the OroraTech extension
 * stops after a few days, and nothing before the last build is precomputed at all.
 */
const gridCoverageHint = computed<string | null>(() => {
  if (mapViewMode.value !== 'grid') return null;
  const coverage = gridCoverage.value;
  if (!coverage) return null;
  const stamp = (ms: number) => formatDateTime(new Date(ms).toISOString());
  return t('timeControls.gridCoverageHint', {
    start: stamp(coverage.startMs),
    allSatellitesEnd: stamp(coverage.allSatellitesEndMs),
    end: stamp(coverage.endMs),
  });
});

const panelVisible = computed(() => props.panelVisible);

const startDateStr = computed(() => toDatetimeLocal(timeRange.value[0]));
const endDateStr = computed(() => toDatetimeLocal(timeRange.value[1]));
const metaStartStr = computed(() => toDatetimeLocal(metaTimeRange.value[0]));
const metaEndStr = computed(() => toDatetimeLocal(metaTimeRange.value[1]));

function onStartChange(e: Event) {
  const val = (e.target as HTMLInputElement).value;
  if (!val) return;
  const ms = fromDatetimeLocal(val);
  if (!isNaN(ms)) timeRange.value = [ms, timeRange.value[1]];
}

function onEndChange(e: Event) {
  const val = (e.target as HTMLInputElement).value;
  if (!val) return;
  const ms = fromDatetimeLocal(val);
  if (!isNaN(ms)) timeRange.value = [timeRange.value[0], ms];
}

function resetTimeRange() {
  timeRange.value = [metaTimeRange.value[0], standardForecastEndMs.value];
}

const presetHours = [6, 12, 24, 48] as const;

function applyPreset(hours: number) {
  const ms = hours * 60 * 60 * 1000;
  const start = Math.max(
    metaTimeRange.value[0],
    Math.min(Date.now(), metaTimeRange.value[1]),
  );
  const end = Math.min(metaTimeRange.value[1], start + ms);
  timeRange.value = [start, end];
}

function setStartToNow() {
  const now = Date.now();
  const clamped = Math.max(metaTimeRange.value[0], Math.min(now, timeRange.value[1]));
  timeRange.value = [clamped, timeRange.value[1]];
}
</script>

<style scoped lang="scss">
.map-time-overlay {
  position: absolute;
  right: calc(var(--mk-control-button-size) + var(--mk-control-spacing) + 0.55rem);
  bottom: calc(var(--mk-footer-height) + var(--mk-control-spacing));
  z-index: 9;
  width: 265px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(12, 35, 43, 0.92);
  color: var(--p-navbar-color, #ffffffcc);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  padding: 0.5rem 0.75rem;
}

.map-time-overlay-fields {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.map-time-overlay-field {
  display: grid;
  grid-template-columns: 38px 1fr;
  align-items: center;
  gap: 0.28rem;
}

.map-time-overlay-label {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.64rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--p-text-muted-color, #8DA0A7);
}

.map-time-overlay-label--with-info {
  gap: 0.1rem;
}

.map-time-overlay-label label {
  cursor: pointer;
}

.map-time-overlay-info {
  width: 1rem;
  min-width: 1rem;
  height: 1rem;
  padding: 0;
  color: var(--p-text-muted-color, #8DA0A7);
  cursor: help;
  font-size: 0.66rem;
}

.map-time-overlay-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  padding: 0.16rem 0.32rem;
  color: var(--p-text-color, #ffffffcc);
  font-size: 0.7rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #03DECC;
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(0.7);
    cursor: pointer;
  }
}

.map-time-overlay-presets {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.35rem;
}

.map-time-overlay-preset-btn {
  flex: 1;
  padding: 0.2rem 0;
  font-size: 0.62rem;
  font-weight: 600;
  font-family: inherit;
  letter-spacing: 0.03em;
  text-align: center;
  color: var(--p-text-color, #ffffffcc);
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(3, 222, 204, 0.15);
    border-color: rgba(3, 222, 204, 0.4);
  }

  &:active {
    background: rgba(3, 222, 204, 0.25);
  }
}

.map-time-overlay-actions {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-top: 0.3rem;
}

.map-time-overlay-now {
  width: auto;
  padding: 0 6px;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  gap: 3px;
}

.map-time-overlay-graph-btn {
  flex: 1;
  min-width: 96px;
  justify-content: center;
  font-size: 0.7rem;
}

:root:not(.color-scheme-dark) {
  .map-time-overlay {
    background: rgba(255, 255, 255, 0.97);
    border-color: rgba(15, 23, 42, 0.12);
    color: #0f172a;
  }

  .map-time-overlay-input {
    background: #ffffff;
    border-color: rgba(15, 23, 42, 0.14);
    color: #0f172a;

    &::-webkit-calendar-picker-indicator {
      filter: none;
    }
  }

  .map-time-overlay-preset-btn {
    background: rgba(15, 23, 42, 0.05);
    border-color: rgba(15, 23, 42, 0.14);
    color: #0f172a;
  }
}

@media (max-width: 1100px) {
  .map-time-overlay {
    right: calc(var(--mk-control-spacing) + var(--mk-control-button-size) + 0.4rem);
    width: 245px;
  }

  .map-time-overlay-graph-btn {
    min-width: 86px;
  }
}

</style>
